import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-expenses-suppliers';
import { header, type Supplier } from '../page/api';

export { addName, type Address, type Site, type Supplier } from '../page/api';

export type SupplierPick = components['schemas']['SupplierPick'];
export type SupplierCreate = components['schemas']['SupplierCreate'];
export type SiteInput = components['schemas']['SiteInput'];
export type SiteHit = components['schemas']['SiteHit'];
export type VatPreview = components['schemas']['VatPreview'];

/** A read: the value, or that it failed (the network, or an answer that is not 2xx). */
export type Read<T> = { readonly ok: true; readonly value: T } | { readonly ok: false };

type Answer<T> = { readonly data?: T | undefined; readonly response: Response };

async function read<T>(call: () => Promise<Answer<T>>): Promise<Read<T>> {
  try {
    const { data, response } = await call();
    return response.ok && data !== undefined ? { ok: true, value: data } : { ok: false };
  } catch {
    return { ok: false };
  }
}

/** The picker's search (`searchSuppliers`): under 2 characters the server answers no items. */
export async function searchSupplierPicks(q: string): Promise<Read<readonly SupplierPick[]>> {
  const outcome = await read(() =>
    apiClient.GET('/v1/expenses/suppliers', {
      params: { header: header(), query: { q, limit: 20 } },
    }),
  );
  return outcome.ok ? { ok: true, value: outcome.value.items } : outcome;
}

/** «Dove hai dormito?»: active sites of suppliers that are not intermediaries. */
export async function searchStaySites(q: string): Promise<Read<readonly SiteHit[]>> {
  const outcome = await read(() =>
    apiClient.GET('/v1/expenses/suppliers/sites', {
      params: { header: header(), query: { q, limit: 20 } },
    }),
  );
  return outcome.ok ? { ok: true, value: outcome.value.items } : outcome;
}

export function getSupplier(supplierId: string): Promise<Read<Supplier>> {
  return read(() =>
    apiClient.GET('/v1/expenses/suppliers/{supplierId}', {
      params: { path: { supplierId }, header: header() },
    }),
  );
}

/** A problem's `code`, `message` and the duplicate's `existing` / `similar`, when present. */
type Problem = {
  readonly status: number;
  readonly code: string | null;
  readonly message: string | null;
  readonly existing: SupplierPick | null;
  readonly similar: readonly SupplierPick[];
};

function problemOf(status: number, error: unknown): Problem {
  const body = (typeof error === 'object' && error !== null ? error : {}) as {
    code?: unknown;
    message?: unknown;
    existing?: unknown;
    similar?: unknown;
  };
  const isPick = (value: unknown): value is SupplierPick =>
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { id?: unknown }).id === 'string' &&
    typeof (value as { name?: unknown }).name === 'string';
  return {
    status,
    code: typeof body.code === 'string' ? body.code : null,
    message: typeof body.message === 'string' ? body.message : null,
    existing: isPick(body.existing) ? body.existing : null,
    similar: Array.isArray(body.similar) ? body.similar.filter(isPick) : [],
  };
}

const NO_ANSWER: Problem = { status: 0, code: null, message: null, existing: null, similar: [] };

export type CreateOutcome =
  | { readonly kind: 'created'; readonly supplier: Supplier }
  /** 409 SUPPLIER_VAT_EXISTS: the VAT number is the existing one's. */
  | { readonly kind: 'vat-exists'; readonly existing: SupplierPick }
  /** 409 SUPPLIER_SIMILAR_NAMES: nothing created; `acknowledgeSimilar` creates it anyway. */
  | { readonly kind: 'similar'; readonly similar: readonly SupplierPick[] }
  | { readonly kind: 'failed'; readonly problem: Problem };

/** `createSupplier`: the supplier is born only here, on the window's confirm (D246). */
export async function createSupplier(body: SupplierCreate): Promise<CreateOutcome> {
  try {
    const { data, error, response } = await apiClient.POST('/v1/expenses/suppliers', {
      params: { header: header() },
      body,
    });
    if (response.ok && data !== undefined) return { kind: 'created', supplier: data };
    const problem = problemOf(response.status, error);
    if (problem.code === 'SUPPLIER_VAT_EXISTS' && problem.existing) {
      return { kind: 'vat-exists', existing: problem.existing };
    }
    if (problem.code === 'SUPPLIER_SIMILAR_NAMES') {
      return { kind: 'similar', similar: problem.similar };
    }
    return { kind: 'failed', problem };
  } catch {
    return { kind: 'failed', problem: NO_ANSWER };
  }
}

export type VatOutcome =
  | { readonly kind: 'checked'; readonly preview: VatPreview }
  /** 422 VAT_INVALID: the number does not fit its country's form. */
  | { readonly kind: 'invalid' }
  /** 503 VIES_UNAVAILABLE, or no answer at all. */
  | { readonly kind: 'unavailable' };

/** `previewSupplierVat`: «Verifica» before «Crea»; nothing is saved. */
export async function previewVat(vatCountry: string, vat: string): Promise<VatOutcome> {
  try {
    const { data, error, response } = await apiClient.POST('/v1/expenses/suppliers/vat-check', {
      params: { header: header() },
      body: { vatCountry, vat },
    });
    if (response.ok && data !== undefined) return { kind: 'checked', preview: data };
    if (problemOf(response.status, error).code === 'VAT_INVALID') return { kind: 'invalid' };
    return { kind: 'unavailable' };
  } catch {
    return { kind: 'unavailable' };
  }
}

/** `addSupplierSite`: the supplier, with the new site. */
export function addSite(supplierId: string, site: SiteInput): Promise<Read<Supplier>> {
  return read(() =>
    apiClient.POST('/v1/expenses/suppliers/{supplierId}/sites', {
      params: { path: { supplierId }, header: header() },
      body: site,
    }),
  );
}

/** The site a write added under `sign`: the last of that sign, else the last site. */
export function siteBySign(supplier: Supplier, sign: string): string | null {
  const named = supplier.sites.filter((site) => site.sign === sign);
  return (named.at(-1) ?? supplier.sites.at(-1))?.id ?? null;
}
